import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme, THEMES } from '../context/ThemeContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import JsBarcode from 'jsbarcode';
import { 
  Truck, Wrench, ClipboardCheck, 
  Coins, Gift, ArrowRight, Settings, 
  LogOut, Palette, Check, Copy,
  Shield, LayoutDashboard, Barcode,
  Home, Map, Menu, X, User, Bell,
  ChevronRight, MapPin, AlertTriangle,
  Plus, Star, Trophy, TrendingUp, MessageCircle,
  RefreshCw, Zap, Target, LayoutGrid
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPS = [
  { 
    app_id: 'ridero', 
    name: 'Ridero', 
    description: 'Delivery dispatch - Hotels, Airports, Drivers',
    icon: Truck,
    color: '#22C55E',
    gradient: 'from-green-500/20 to-green-600/5',
    logo: '/logos/ridero.jpg',
    url: 'https://www.rideroexpress.com/login'
  },
  { 
    app_id: 'mechanic_kiosk', 
    name: 'MechanicKiosk', 
    description: 'Touchscreen for ordering truck parts',
    icon: Wrench,
    color: '#3B82F6',
    gradient: 'from-blue-500/20 to-blue-600/5',
    logo: '/logos/peoria.jpg',
    url: 'https://peoriakiosk.com/login'
  },
  { 
    app_id: 'pti_pector', 
    name: 'PTI Pector', 
    description: 'Truck inspection - locations, plates, issues',
    icon: ClipboardCheck,
    color: '#F97316',
    gradient: 'from-orange-500/20 to-orange-600/5',
    logo: '/logos/pti_pector.png',
    url: 'https://ptipector.com/dashboard'
  },
];

// Progress Ring Component
const ProgressRing = ({ progress, size = 80, strokeWidth = 6 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          className="text-muted/30"
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="text-accent transition-all duration-1000 ease-out"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold">{Math.round(progress)}%</span>
      </div>
    </div>
  );
};

// Star Rating Component
const StarRating = ({ rating, onRate, readonly = false, size = 'md' }) => {
  const [hover, setHover] = useState(0);
  const sizeClass = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5';
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && onRate?.(star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => setHover(0)}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer'} transition-transform hover:scale-110`}
        >
          <Star
            className={`${sizeClass} ${
              star <= (hover || rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-muted-foreground/30'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

export default function HubPage() {
  const { user, logout, refreshUser } = useAuth();
  const { theme, setTheme, themes, background, setBackground, currentTheme, currentBackground } = useTheme();
  const navigate = useNavigate();
  const [showThemes, setShowThemes] = useState(false);
  const [showBackgrounds, setShowBackgrounds] = useState(false);
  const [stats, setStats] = useState(null);
  const [showBarcode, setShowBarcode] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showFab, setShowFab] = useState(false);
  const [flippedCard, setFlippedCard] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [weeklyProgress, setWeeklyProgress] = useState(0);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingUser, setRatingUser] = useState(null);
  const [newRating, setNewRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');

  // Pull to refresh
  const handlePullRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([fetchStats(), refreshUser()]);
      toast.success('Refreshed!');
    } catch (error) {
      toast.error('Refresh failed');
    }
    setIsRefreshing(false);
  }, [refreshUser]);

  useEffect(() => {
    fetchStats();
    fetchActivityFeed();
    fetchUserRank();
    
    // Check if app is already installed
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || localStorage.getItem('hub_installed');
    
    if (!isInstalled) {
      setShowInstallBanner(true);
    }
    
    // Listen for PWA install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Pull to refresh on mobile
    let startY = 0;
    const handleTouchStart = (e) => { startY = e.touches[0].clientY; };
    const handleTouchMove = (e) => {
      const y = e.touches[0].clientY;
      if (y - startY > 100 && window.scrollY === 0 && !isRefreshing) {
        handlePullRefresh();
      }
    };
    
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [handlePullRefresh, isRefreshing]);

  const fetchActivityFeed = async () => {
    try {
      const res = await axios.get(`${API}/events?limit=5`, { withCredentials: true });
      const data = res.data || [];
      // Use mock data if no events exist (better UX)
      if (data.length === 0) {
        setActivityFeed([
          { id: 1, type: 'points_earned', message: 'Completed delivery', points: 10, time: '2m ago' },
          { id: 2, type: 'login', message: 'Logged in via Hub', points: 5, time: '1h ago' },
          { id: 3, type: 'referral', message: 'Referral bonus', points: 50, time: '2h ago' },
        ]);
      } else {
        setActivityFeed(data);
      }
    } catch (error) {
      // Use mock data if endpoint doesn't exist
      setActivityFeed([
        { id: 1, type: 'points_earned', message: 'Completed delivery', points: 10, time: '2m ago' },
        { id: 2, type: 'login', message: 'Logged in via Hub', points: 5, time: '1h ago' },
        { id: 3, type: 'referral', message: 'Referral bonus', points: 50, time: '2h ago' },
      ]);
    }
  };

  const fetchUserRank = async () => {
    try {
      const res = await axios.get(`${API}/users/rank`, { withCredentials: true });
      setUserRank(res.data);
      setWeeklyProgress(res.data?.weekly_progress || 65);
    } catch (error) {
      // Mock data
      setUserRank({ rank: 5, total: 100, weekly_points: 150, weekly_goal: 200 });
      setWeeklyProgress(75);
    }
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        localStorage.setItem('hub_installed', 'true');
        setShowInstallBanner(false);
        toast.success('🎉 +50 points! App installed successfully!');
        // TODO: Award points via API
      }
    } else {
      // Show manual instructions
      toast.info('📱 Tap Share → Add to Home Screen');
    }
  };

  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem('hub_install_dismissed', 'true');
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // Generate barcode SVG when showing
  useEffect(() => {
    if (showBarcode && user?.barcode) {
      // Small delay to ensure SVG is in DOM
      const timer = setTimeout(() => {
        try {
          const svg = document.getElementById("barcode-svg");
          if (svg) {
            JsBarcode(svg, user.barcode, {
              format: "CODE128",
              width: 2,
              height: 80,
              displayValue: true,
              fontSize: 14,
              background: "#ffffff",
              lineColor: "#000000",
              margin: 10
            });
          }
        } catch (e) {
          console.error("Barcode generation error:", e);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showBarcode, user?.barcode]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleAppClick = async (appId) => {
    try {
      // Find the app config
      const app = APPS.find(a => a.app_id === appId);
      
      if (!app) {
        toast.error(`App ${appId} not found`);
        return;
      }
      
      // Get SSO token for the app
      const res = await axios.get(`${API}/sso/token?app_id=${appId}`, { withCredentials: true });
      const ssoToken = res.data.token;
      
      if (!ssoToken) {
        toast.error('Failed to generate SSO token');
        return;
      }
      
      if (app.url) {
        // Build redirect URL with SSO token
        const redirectUrl = `${app.url}?session_token=${ssoToken}`;
        toast.success(`Redirecting to ${app.name}...`);
        
        // Open in new tab
        const newWindow = window.open(redirectUrl, '_blank');
        
        // Check if popup was blocked
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          toast.info(`Popup blocked! Click here to open ${app.name}`, {
            action: {
              label: 'Open',
              onClick: () => window.open(redirectUrl, '_blank')
            },
            duration: 10000
          });
        }
      } else {
        toast.info(`${app.name} URL not configured yet. SSO token ready!`);
      }
      
      // Refresh user data
      await refreshUser();
      fetchStats();
    } catch (error) {
      console.error('SSO Error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to connect: ${errorMsg}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(user?.referral_code || '');
    toast.success('Referral code copied!');
  };

  const isMasterAdmin = user?.role_id === 'master_admin';

  return (
    <div className={`min-h-screen ${currentBackground?.style || 'bg-background'} relative overflow-hidden transition-all duration-500`}>
      {/* Background effects */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent/10 rounded-full blur-3xl" />

      {/* Install Banner */}
      {showInstallBanner && !localStorage.getItem('hub_install_dismissed') && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-accent/90 to-primary/90 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white text-sm">
              <span className="text-lg">📲</span>
              <span className="hidden sm:inline">Add to Home Screen</span>
              <span className="sm:hidden">Install App</span>
              <span className="font-bold text-yellow-300">+50 pts!</span>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                variant="secondary" 
                className="h-7 text-xs bg-white/20 hover:bg-white/30 text-white border-0"
                onClick={handleInstallClick}
              >
                Install
              </Button>
              <button 
                onClick={dismissInstallBanner}
                className="text-white/70 hover:text-white p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className={`glass-header sticky ${showInstallBanner && !localStorage.getItem('hub_install_dismissed') ? 'top-10' : 'top-0'} z-50 transition-all`}>
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/logo-full.png" 
              alt="Leader Ultimate Hub" 
              className="h-12 w-12 object-contain drop-shadow-[0_0_8px_rgba(218,165,32,0.5)]"
            />
            <span className="font-bold text-lg font-secondary uppercase tracking-tight hidden sm:block">Leader Ultimate Hub</span>
          </div>

          <div className="flex items-center gap-4">
            {/* Points */}
            <div className="flex items-center gap-2 px-4 py-2 bg-card/50 rounded-lg border border-border/50">
              <Coins className="w-5 h-5 text-accent" />
              <span className="font-semibold">{user?.points_balance?.toLocaleString()}</span>
              <span className="text-sm text-muted-foreground">pts</span>
            </div>

            {/* Role badge */}
            <Badge className={`role-badge role-${user?.role_id}`}>
              {user?.role_id?.replace('_', ' ')}
            </Badge>

            {/* Barcode button */}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowBarcode(!showBarcode)}
              data-testid="barcode-btn"
            >
              <Barcode className="w-4 h-4 mr-2" />
              My Barcode
            </Button>

            {/* Theme toggle */}
            <div className="relative">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setShowThemes(!showThemes)}
                data-testid="hub-theme-btn"
              >
                <Palette className="w-5 h-5" />
              </Button>
              
              {showThemes && (
                <div className="absolute right-0 top-12 p-4 bg-card border border-border rounded-lg shadow-xl z-50 animate-fade-in-up min-w-[200px]">
                  {/* Theme Selection */}
                  <p className="text-xs text-muted-foreground mb-2 font-medium">Theme</p>
                  <div className="flex gap-2 mb-4">
                    {Object.values(themes).map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setTheme(t.id)}
                        className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center transition-all ${
                          theme === t.id ? 'border-accent scale-110' : 'border-border'
                        }`}
                        style={{ background: t.colors.bg }}
                        title={t.name}
                      >
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.colors.accent }} />
                      </button>
                    ))}
                  </div>
                  
                  {/* Background Selection */}
                  <p className="text-xs text-muted-foreground mb-2 font-medium">Background</p>
                  <div className="flex gap-2">
                    {currentTheme?.backgrounds?.map((bg) => (
                      <button
                        key={bg.id}
                        onClick={() => setBackground(bg.id)}
                        className={`px-3 py-1.5 rounded-md border-2 text-xs font-medium transition-all ${
                          background === bg.id 
                            ? 'border-accent bg-accent/20 text-accent' 
                            : 'border-border hover:border-muted-foreground'
                        }`}
                        title={bg.name}
                      >
                        {bg.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Admin dashboard link */}
            {isMasterAdmin && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/dashboard')}
                data-testid="admin-dashboard-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Admin
              </Button>
            )}

            {/* Logout */}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
        <div className="accent-line" />
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-6 py-12 relative z-10">
        {/* Barcode Modal */}
        {showBarcode && (
          <div 
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowBarcode(false)}
          >
            <div 
              className="bg-card border border-border rounded-xl p-8 max-w-md w-full animate-fade-in-up"
              onClick={(e) => e.stopPropagation()}
              data-testid="barcode-modal"
            >
              <div className="text-center">
                <h3 className="text-xl font-bold font-secondary uppercase mb-2">Your Barcode</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Scan at kiosks for quick check-in
                </p>
                
                <div className="bg-white rounded-lg p-4 mb-4">
                  <svg id="barcode-svg"></svg>
                </div>
                
                <p className="font-mono text-lg mb-6">{user?.barcode}</p>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      navigator.clipboard.writeText(user?.barcode || '');
                      toast.success('Barcode copied!');
                    }}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                  <Button 
                    className="flex-1"
                    onClick={() => setShowBarcode(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Welcome section */}
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl font-bold font-secondary uppercase tracking-tight mb-4">
            Welcome, {user?.name?.split(' ')[0]}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Choose an app to continue. Your account is automatically logged in across all connected platforms.
          </p>
        </div>

        {/* App selector */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {APPS.map((app, idx) => {
            const Icon = app.icon;
            const isConnected = user?.connected_apps?.includes(app.app_id);
            
            return (
              <div
                key={app.app_id}
                className={`hub-app-card animate-fade-in-up animate-stagger-${idx + 1}`}
                style={{ '--app-color': app.color }}
                onClick={() => handleAppClick(app.app_id)}
                data-testid={`app-card-${app.app_id}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${app.gradient} opacity-50`} />
                
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-6">
                    {app.logo ? (
                      <div 
                        className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center"
                        style={{ backgroundColor: `${app.color}20` }}
                      >
                        <img 
                          src={app.logo} 
                          alt={`${app.name} logo`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div 
                        className="w-16 h-16 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${app.color}20` }}
                      >
                        <Icon className="w-8 h-8" style={{ color: app.color }} strokeWidth={1.5} />
                      </div>
                    )}
                    <div className={`status-dot ${isConnected ? 'online' : 'offline'}`} />
                  </div>
                  
                  <h3 className="text-2xl font-bold font-secondary uppercase tracking-tight mb-2">
                    {app.name}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {app.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <Badge variant={isConnected ? 'default' : 'secondary'}>
                      {isConnected ? 'Connected' : 'Not Connected'}
                    </Badge>
                    <ArrowRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Points Wallet */}
          <Card className="glass border-border/50 card-hover animate-fade-in-up animate-stagger-4" data-testid="points-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-secondary uppercase">
                <Coins className="w-5 h-5 text-accent" />
                Points Wallet
              </CardTitle>
              <CardDescription>Your unified loyalty balance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="points-glow inline-block px-6 py-4 bg-card rounded-lg mb-4">
                <span className="text-5xl font-bold text-accent">
                  {user?.points_balance?.toLocaleString()}
                </span>
                <span className="text-xl text-muted-foreground ml-2">pts</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Earn points across all connected apps. Use them for rewards and perks.
              </p>
            </CardContent>
          </Card>

          {/* Referral */}
          <Card className="glass border-border/50 card-hover animate-fade-in-up animate-stagger-5" data-testid="referral-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-secondary uppercase">
                <Gift className="w-5 h-5 text-accent" />
                Referral Program
              </CardTitle>
              <CardDescription>Invite friends, earn 50 points each</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <div className="flex-1 px-4 py-3 bg-secondary/50 rounded-lg font-mono text-lg">
                  {user?.referral_code}
                </div>
                <Button variant="outline" size="icon" onClick={copyReferralCode} data-testid="copy-referral-btn">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Share your code with friends. When they sign up, you both get 50 points!
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Weekly Progress & Rank Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Weekly Progress */}
          <Card className="glass border-border/50 animate-fade-in-up" data-testid="progress-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-secondary uppercase">
                <Target className="w-5 h-5 text-accent" />
                Weekly Goal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <ProgressRing progress={weeklyProgress} size={100} strokeWidth={8} />
                <div>
                  <p className="text-2xl font-bold">{userRank?.weekly_points || 0} pts</p>
                  <p className="text-sm text-muted-foreground">of {userRank?.weekly_goal || 200} goal</p>
                  <p className="text-xs text-accent mt-2">Keep going! 💪</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rank Badge */}
          <Card className="glass border-border/50 animate-fade-in-up" data-testid="rank-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-secondary uppercase">
                <Trophy className="w-5 h-5 text-yellow-500" />
                Your Rank
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center border-2 border-yellow-500/50">
                  <span className="text-3xl font-bold text-yellow-500">#{userRank?.rank || '?'}</span>
                </div>
                <div>
                  <p className="text-lg font-semibold">Top {userRank?.rank || '?'} of {userRank?.total || '100'}</p>
                  <p className="text-sm text-muted-foreground">Keep earning to climb! 🚀</p>
                  <div className="flex items-center gap-1 mt-2">
                    <TrendingUp className="w-4 h-4 text-green-500" />
                    <span className="text-xs text-green-500">+3 positions this week</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activity Feed */}
        <Card className="glass border-border/50 mb-12 animate-fade-in-up" data-testid="activity-feed">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 font-secondary uppercase">
                <Zap className="w-5 h-5 text-accent" />
                Recent Activity
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={handlePullRefresh} disabled={isRefreshing}>
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activityFeed.length > 0 ? activityFeed.map((activity, idx) => (
                <div key={activity.id || idx} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                      <Coins className="w-4 h-4 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{activity.message}</p>
                      <p className="text-xs text-muted-foreground">{activity.time}</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-green-500">+{activity.points} pts</span>
                </div>
              )) : (
                <p className="text-center text-muted-foreground py-4">No recent activity</p>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Floating Action Button */}
      <div className="fixed bottom-24 right-6 z-40 md:bottom-8">
        <div className={`flex flex-col-reverse gap-2 items-center transition-all ${showFab ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <Button 
            size="icon" 
            className="w-12 h-12 rounded-full shadow-lg bg-green-500 hover:bg-green-600"
            onClick={() => navigate('/apps')}
          >
            <LayoutGrid className="w-5 h-5" />
          </Button>
          <Button 
            size="icon" 
            className="w-12 h-12 rounded-full shadow-lg bg-blue-500 hover:bg-blue-600"
            onClick={() => setShowBarcode(true)}
          >
            <Barcode className="w-5 h-5" />
          </Button>
          <Button 
            size="icon" 
            className="w-12 h-12 rounded-full shadow-lg bg-purple-500 hover:bg-purple-600"
            onClick={() => setShowRatingModal(true)}
          >
            <Star className="w-5 h-5" />
          </Button>
        </div>
        <Button 
          size="icon" 
          className="w-14 h-14 rounded-full shadow-xl bg-accent hover:bg-accent/90 mt-2"
          onClick={() => setShowFab(!showFab)}
        >
          <Plus className={`w-6 h-6 transition-transform ${showFab ? 'rotate-45' : ''}`} />
        </Button>
      </div>

      {/* Rating Modal */}
      {showRatingModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end md:items-center justify-center"
          onClick={() => setShowRatingModal(false)}
        >
          <div 
            className="bg-card border border-border rounded-t-3xl md:rounded-xl p-6 w-full max-w-md animate-slide-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-1 bg-muted rounded-full mx-auto mb-4 md:hidden" />
            <h3 className="text-xl font-bold font-secondary uppercase mb-4 text-center">Rate & Review</h3>
            
            <div className="text-center mb-6">
              <p className="text-muted-foreground mb-3">How was your experience?</p>
              <div className="flex justify-center">
                <StarRating rating={newRating} onRate={setNewRating} size="lg" />
              </div>
            </div>
            
            <textarea
              className="w-full p-3 rounded-lg bg-secondary/50 border border-border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-accent"
              rows={3}
              placeholder="Leave a comment (optional)..."
              value={ratingComment}
              onChange={(e) => setRatingComment(e.target.value)}
            />
            
            <div className="flex gap-3 mt-4">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowRatingModal(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1"
                onClick={() => {
                  toast.success(`Thanks for your ${newRating}-star rating! 🌟`);
                  setShowRatingModal(false);
                  setNewRating(0);
                  setRatingComment('');
                }}
                disabled={newRating === 0}
              >
                Submit
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pull to Refresh Indicator */}
      {isRefreshing && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-accent text-accent-foreground px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-sm">Refreshing...</span>
          </div>
        </div>
      )}

      {/* Mobile Sliding Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div 
            className="absolute right-0 top-0 h-full w-80 bg-card border-l border-border shadow-2xl animate-slide-in-right"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Menu Header */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between mb-4">
                <img 
                  src="/logo-full.png" 
                  alt="Leader Ultimate Hub" 
                  className="w-10 h-10 object-contain drop-shadow-[0_0_8px_rgba(218,165,32,0.5)]"
                />
                <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              {/* User Profile */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                  <User className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <p className="font-semibold">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.role_id}</p>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="p-4 space-y-2">
              <button 
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors"
                onClick={() => { setShowBarcode(true); setMobileMenuOpen(false); }}
              >
                <Barcode className="w-5 h-5 text-accent" />
                <span>My Barcode</span>
                <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
              </button>

              <button 
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors"
                onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
              >
                <LayoutDashboard className="w-5 h-5 text-accent" />
                <span>Dashboard</span>
                <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
              </button>

              <button 
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors"
                onClick={() => setShowThemes(!showThemes)}
              >
                <Palette className="w-5 h-5 text-accent" />
                <span>Themes & Backgrounds</span>
                <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
              </button>

              {showThemes && (
                <div className="pl-4 py-2 space-y-3">
                  {/* Theme Selection */}
                  <p className="text-xs text-muted-foreground font-medium">Theme</p>
                  <div className="flex gap-2">
                    {Object.values(themes).map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setTheme(t.id)}
                        className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center transition-all ${
                          theme === t.id ? 'border-accent scale-110' : 'border-border'
                        }`}
                        style={{ background: t.colors.bg }}
                        title={t.name}
                      >
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.colors.accent }} />
                      </button>
                    ))}
                  </div>
                  
                  {/* Background Selection */}
                  <p className="text-xs text-muted-foreground font-medium mt-3">Background</p>
                  <div className="flex gap-2 flex-wrap">
                    {currentTheme?.backgrounds?.map((bg) => (
                      <button
                        key={bg.id}
                        onClick={() => setBackground(bg.id)}
                        className={`px-3 py-1.5 rounded-md border-2 text-xs font-medium transition-all ${
                          background === bg.id 
                            ? 'border-accent bg-accent/20 text-accent' 
                            : 'border-border hover:border-muted-foreground'
                        }`}
                      >
                        {bg.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {isMasterAdmin && (
                <button 
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors"
                  onClick={() => { navigate('/approvals'); setMobileMenuOpen(false); }}
                >
                  <Shield className="w-5 h-5 text-accent" />
                  <span>User Approvals</span>
                  {stats?.pendingApprovals > 0 && (
                    <Badge variant="destructive" className="ml-auto">{stats.pendingApprovals}</Badge>
                  )}
                </button>
              )}

              <div className="border-t border-border my-4" />

              <button 
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-destructive/10 text-destructive transition-colors"
                onClick={handleLogout}
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>

            {/* Quick Actions */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border bg-card">
              <p className="text-xs text-muted-foreground mb-3 uppercase tracking-wider">Quick Actions</p>
              <div className="grid grid-cols-3 gap-2">
                {APPS.map(app => {
                  const Icon = app.icon;
                  return (
                    <button
                      key={app.app_id}
                      className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-secondary/50 transition-colors"
                      onClick={() => { handleAppClick(app.app_id); setMobileMenuOpen(false); }}
                    >
                      {app.logo ? (
                        <img src={app.logo} alt={app.name} className="w-8 h-8 rounded-lg object-cover" />
                      ) : (
                        <Icon className="w-6 h-6" style={{ color: app.color }} />
                      )}
                      <span className="text-xs">{app.name.split(/(?=[A-Z])/).join(' ')}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur-lg border-t border-border z-40 md:hidden safe-area-bottom">
        <div className="flex items-center justify-around py-2">
          <button 
            className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${activeTab === 'home' ? 'text-accent' : 'text-muted-foreground'}`}
            onClick={() => setActiveTab('home')}
          >
            <Home className="w-5 h-5" />
            <span className="text-xs">Home</span>
          </button>

          <button 
            className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${activeTab === 'apps' ? 'text-accent' : 'text-muted-foreground'}`}
            onClick={() => setActiveTab('apps')}
          >
            <LayoutGrid className="w-5 h-5" />
            <span className="text-xs">Apps</span>
          </button>

          <button 
            className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${activeTab === 'points' ? 'text-accent' : 'text-muted-foreground'}`}
            onClick={() => setActiveTab('points')}
          >
            <Coins className="w-5 h-5" />
            <span className="text-xs">Points</span>
          </button>

          <button 
            className="flex flex-col items-center gap-1 p-2 rounded-lg text-muted-foreground transition-colors"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu className="w-5 h-5" />
            <span className="text-xs">Menu</span>
          </button>
        </div>
      </nav>

      {/* Add padding at bottom for mobile nav */}
      <div className="h-20 md:hidden" />
    </div>
  );
}
